import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context';
import portalService from '../../services/portalService';
import PortalLogo from '../../components/PortalLogo';
import { PRIMARY } from '../../utils/portalWordmark';
import './portal.css';
import './portal-screens.css';
import { BRAND_URL } from '../../utils/brand';

/**
 * PortalShell — wraps every Portal Noir route in the black/cream/gold register
 * (Brand Protocol v3.1 redesign: Cormorant Garamond + IBM Plex Mono, the
 * velvet-rope layer). Drops the global Portal Navbar; Portal Noir has its own
 * minimal top bar.
 *
 * THE GOLD RULE (kintsugi): gold marks only what is live, chosen, or yours.
 * In the nav that means the *active route* gets the gold seam — nothing else,
 * Admin included.
 */
export default function PortalShell({ children, hideTopBar = false, viewerState }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [serverPortalRole, setServerPortalRole] = useState(null);

  // Fetch the server-authoritative role so the ADMIN link appears for admins
  // and owners. Stale JWT payloads previously caused this link to vanish for
  // members promoted after their last login.
  useEffect(() => {
    if (!isAuthenticated || viewerState !== 'accepted') return;
    let cancelled = false;
    portalService.getState().then((r) => {
      if (cancelled) return;
      if (r.success) setServerPortalRole(r.data?.portalRole || null);
    });
    return () => { cancelled = true; };
  }, [isAuthenticated, viewerState]);

  const isAdmin = serverPortalRole === 'admin' || serverPortalRole === 'owner';
  const path = location.pathname;

  // Active-route detection drives the single gold seam.
  const sendActive = path === '/noir/signals/new';
  const giveTakeActive = path.startsWith('/noir/give-take');
  const signalsActive = !sendActive && !giveTakeActive && (path.startsWith('/noir/home') || path.startsWith('/noir/signals'));
  const membersActive = path.startsWith('/noir/members');
  const adminActive = path.startsWith('/noir/admin');
  const aboutActive = path.startsWith('/noir/about');
  const applyActive = path.startsWith('/noir/apply');

  const navClass = (active) => `portal-nav-item${active ? ' portal-nav-item--active' : ''}`;

  return (
    <div className="portal-scope">
      {!hideTopBar && (
        <div className="portal-topbar">
          <div className="portal-wordmark">
            {/* The primary lockup: the spinning portal IS the O. It is still two
                destinations, as it always was here — the word goes to the Portal Noir
                home (members land on the Signals feed), the portal backs out to the
                regular Portal featured wall — so the exit link is laid over the O's
                slot as a sibling rather than nested inside the other link. The
                drawn word is white or black, never a tint, so it does not take Noir's
                cream; NOIR, the sub-brand, stays in this scope's own type, because
                only the six letters of PORTAL exist as geometry. */}
            <span style={{ position: 'relative', display: 'inline-flex', color: '#FFFFFF' }}>
              <Link
                to={viewerState === 'accepted' ? '/noir/home' : '/noir'}
                aria-label="Portal Noir"
                style={{ display: 'inline-flex', color: 'inherit' }}
              >
                <PortalLogo cap={24} />
              </Link>
              <Link
                to="/featured"
                aria-label="Back to Portal"
                style={{
                  position: 'absolute', top: 0, height: '100%',
                  left: `${PRIMARY.o.left}%`, width: `${PRIMARY.o.width}%`,
                }}
              />
            </span>
            <Link
              to={viewerState === 'accepted' ? '/noir/home' : '/noir'}
              aria-hidden
              tabIndex={-1}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: 'inherit' }}
            >
              NOIR
              <span className="portal-tier">MEMBERS</span>
            </Link>
          </div>
          <nav style={{ display: 'flex', alignItems: 'center', gap: 30 }}>
            {viewerState === 'accepted' ? (
              <>
                <Link to="/noir/home" className={navClass(signalsActive)}>HOME</Link>
                <Link to="/noir/members" className={navClass(membersActive)}>MEMBERS</Link>
                <Link to="/noir/signals/new" className={navClass(sendActive)}>SEND A SIGNAL</Link>
                <Link to="/noir/give-take" className={navClass(giveTakeActive)}>GIVE / TAKE</Link>
                {isAdmin && <Link to="/noir/admin" className={navClass(adminActive)}>ADMIN</Link>}
              </>
            ) : (
              <>
                <Link to="/noir/about" className={navClass(aboutActive)}>ABOUT</Link>
                {isAuthenticated ? (
                  <Link to="/noir/apply" className={navClass(applyActive)}>APPLY</Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate('/login?redirect=/portal/apply')}
                    className="portal-nav-item"
                  >
                    LOG IN
                  </button>
                )}
              </>
            )}
          </nav>
        </div>
      )}
      <main>{children}</main>
      {!hideTopBar && (
        <footer style={{ borderTop: '1px solid rgba(236,231,218,0.07)', padding: '26px 0', textAlign: 'center' }}>
          {viewerState === 'accepted' ? (
            <span className="portal-mono" style={{ fontSize: 10, letterSpacing: '0.24em', color: 'rgba(236,231,218,0.34)' }}>
              PORTAL NOIR · BY INVITATION
            </span>
          ) : (
            <span className="portal-mono" style={{ fontSize: 11, letterSpacing: '0.14em', color: 'rgba(236,231,218,0.34)' }}>
              A Private Club Powered by{' '}
              <a href={BRAND_URL} target="_blank" rel="noopener noreferrer" className="portal-gold" style={{ textDecoration: 'none' }}>Portal</a>.
            </span>
          )}
        </footer>
      )}
    </div>
  );
}
